import * as React from 'react';

import type {EntitySummaryRating} from 'nutshell-graphql-types';

import {IconButtonWithConfirmation} from '../button/icon-button/icon-button-with-confirmation';
import {IconOnlyButton} from '../button/icon-only-button/icon-only-button';
import {ChatBubbleRegularIcon} from '../icon/svg/chat-bubble-regular-icon';
import {CogRegularIcon} from '../icon/svg/cog-regular-icon';
import {InfoCircleRegularIcon} from '../icon/svg/info-circle-regular-icon';
// @ts-expect-error
import {RetryRegularIcon} from '../icon/svg/retry-regular-icon';
import {ThumbsDownRegularIcon} from '../icon/svg/thumbs-down-regular-icon';
import {ThumbsUpRegularIcon} from '../icon/svg/thumbs-up-regular-icon';
import {TrashRegularIcon} from '../icon/svg/trash-regular-icon';
import {Stack} from '../layout/stack/stack';
import {SavedSettingToast} from '../saved-setting-toast/saved-setting-toast';
import {useManageSaveState} from '../saved-setting-toast/use-manage-save-state';

import {AiBadge} from './ai-badge';

type Props = {
    userRating?: EntitySummaryRating | null | undefined;
    onRate?: (rating: EntitySummaryRating) => Promise<any>;
    isSavingRating?: boolean;
    onRefresh?: () => Promise<any>;
    onDelete?: () => void;
    onFeedback?: () => void;
    onLearnMore?: () => void;
    onSettingsClick?: () => void;
};

export function Footer(props: Props) {
    const isPositiveRated = props.userRating === 'POSITIVE';
    const isNegativeRated = props.userRating === 'NEGATIVE';

    const {toastState} = useManageSaveState(props.isSavingRating || false, 1000);

    return (
        <Stack justifyContent='space-between' isHorizontal={true}>
            {/* @ts-expect-error */}
            <Stack isHorizontal={true} spacing={4} alignCenter={true}>
                {props.onRefresh && (
                    <IconOnlyButton
                        icon={RetryRegularIcon}
                        onClick={() => {
                            if (props.onRefresh) {
                                props.onRefresh();
                            }
                        }}
                        tooltipText='Refresh summary'
                    />
                )}
                <IconOnlyButton
                    icon={ThumbsUpRegularIcon}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (props.onRate) {
                            // @ts-expect-error
                            props.onRate('POSITIVE');
                        }
                    }}
                    tooltipText='Good summary'
                    isActive={isPositiveRated}
                    isDisabled={!props.onRate || isPositiveRated || props.isSavingRating}
                    variant='purple'
                    hoverTilt='left'
                />
                <IconOnlyButton
                    icon={ThumbsDownRegularIcon}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (props.onRate) {
                            // @ts-expect-error
                            props.onRate('NEGATIVE');
                        }
                    }}
                    tooltipText='Poor summary'
                    isActive={isNegativeRated}
                    isDisabled={!props.onRate || isNegativeRated || props.isSavingRating}
                    variant='purple'
                    hoverTilt='right'
                />
                {props.onFeedback && (
                    <IconOnlyButton
                        icon={ChatBubbleRegularIcon}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (props.onFeedback) {
                                props.onFeedback();
                            }
                        }}
                        tooltipText='Give feedback'
                    />
                )}
                {props.onLearnMore && (
                    <IconOnlyButton
                        icon={InfoCircleRegularIcon}
                        onClick={(e) => {
                            e.stopPropagation();
                            props.onLearnMore?.();
                        }}
                        tooltipText='What’s this?'
                    />
                )}
                {props.onSettingsClick && (
                    <IconOnlyButton
                        icon={CogRegularIcon}
                        onClick={(e) => {
                            e.stopPropagation();
                            props.onSettingsClick?.();
                        }}
                        tooltipText='Your research settings'
                    />
                )}
                {props.onDelete && (
                    <IconButtonWithConfirmation
                        onConfirm={() => {
                            if (props.onDelete) {
                                props.onDelete();
                            }
                        }}
                        icon={TrashRegularIcon}
                        tooltipText='Remove summary'
                        question='Are you sure you want to remove this summary?'
                        dialogWidth='240px'
                        noHoverBackground={true}
                        noPadding={true}
                        iconHoverWhenConfirming={true}
                    />
                )}
                <SavedSettingToast
                    savedText='Thank you!'
                    state={toastState}
                    autoResetDelay={1500}
                    largerSpinner={true}
                />
            </Stack>
            <AiBadge />
        </Stack>
    );
}
